import React, { useState, useEffect } from 'react';
import { billAPI } from '../../services/api';
import { Button } from '../ui/button';
import { X, Search, Calendar, DollarSign, Users } from 'lucide-react';

const AdminProfitDashboard = ({ onClose }) => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        search: ''
    });

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await billAPI.getAdminProfitStats(filters);
            setStats(response.data);
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 403) {
                setError("Access Denied: You do not have permission to view this report.");
            } else {
                setError("Failed to fetch profit statistics.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchStats();
    };

    const totalProfit = stats.reduce((acc, curr) => acc + (curr.totalProfit || 0), 0);
    const totalSales = stats.reduce((acc, curr) => acc + (curr.totalSalesCount || 0), 0);

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-zinc-900 w-full max-w-6xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] border border-zinc-200 dark:border-zinc-800">

                {/* Header */}
                <div className="p-6 border-b border-zinc-200 dark:border-zinc-800 flex justify-between items-center bg-zinc-50/50 dark:bg-zinc-900/50 rounded-t-2xl">
                    <div>
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent">
                            Global Profit & Performance
                        </h2>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                            Team performance analysis and profit distribution
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors">
                        <X className="w-5 h-5 text-zinc-500" />
                    </button>
                </div>

                {/* Filters & Stats */}
                <div className="p-4 md:p-6 space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-violet-50 dark:bg-violet-900/20 p-4 rounded-xl border border-violet-100 dark:border-violet-800 flex items-center gap-4">
                            <div className="p-3 bg-violet-100 dark:bg-violet-800 rounded-lg">
                                <Users className="w-6 h-6 text-violet-600 dark:text-violet-300" />
                            </div>
                            <div>
                                <p className="text-sm text-zinc-500 dark:text-zinc-400">Total Staff</p>
                                <p className="text-2xl font-bold text-violet-700 dark:text-violet-300">{stats.length}</p>
                            </div>
                        </div>
                        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl border border-emerald-100 dark:border-emerald-800 flex items-center gap-4">
                            <div className="p-3 bg-emerald-100 dark:bg-emerald-800 rounded-lg">
                                <DollarSign className="w-6 h-6 text-emerald-600 dark:text-emerald-300" />
                            </div>
                            <div>
                                <p className="text-sm text-zinc-500 dark:text-zinc-400">Total Profit Earned</p>
                                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                                    ₹{totalProfit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800 flex items-center gap-4">
                            <div className="p-3 bg-blue-100 dark:bg-blue-800 rounded-lg">
                                <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                            </div>
                            <div>
                                <p className="text-sm text-zinc-500 dark:text-zinc-400">Total Sales Count</p>
                                <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{totalSales}</p>
                            </div>
                        </div>
                    </div>

                    {/* Filter Bar */}
                    <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end bg-zinc-50 dark:bg-zinc-800/50 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800">
                        <div className="flex-1 min-w-[200px]">
                            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5 block">Search User</label>
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-3 text-zinc-400" />
                                <input
                                    type="text"
                                    name="search"
                                    value={filters.search}
                                    onChange={handleFilterChange}
                                    placeholder="Name or Username..."
                                    className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 focus:ring-2 focus:ring-violet-500 outline-none transition-all"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5 block">Start Date</label>
                            <input
                                type="date"
                                name="startDate"
                                value={filters.startDate}
                                onChange={handleFilterChange}
                                className="px-4 py-2.5 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 outline-none focus:ring-2 focus:ring-violet-500"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5 block">End Date</label>
                            <input
                                type="date"
                                name="endDate"
                                value={filters.endDate}
                                onChange={handleFilterChange}
                                className="px-4 py-2.5 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 outline-none focus:ring-2 focus:ring-violet-500"
                            />
                        </div>
                        <Button type="submit" variant="default" className="bg-violet-600 hover:bg-violet-700">Apply Filters</Button>
                    </form>
                </div>

                {/* Table Content */}
                <div className="flex-1 overflow-auto p-6 pt-0">
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
                        </div>
                    ) : error ? (
                        <div className="text-center p-8 bg-red-50 dark:bg-red-900/10 rounded-xl text-red-600 dark:text-red-400">
                            <p className="font-semibold">{error}</p>
                        </div>
                    ) : (
                        <>
                            {/* Mobile Card View */}
                            <div className="space-y-4 md:hidden">
                                {stats.length > 0 ? (
                                    stats.map((row, idx) => (
                                        <div key={idx} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4 rounded-xl shadow-sm flex flex-col gap-3">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{row.fullName}</h3>
                                                    <span className="text-xs text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 rounded-md mt-1 inline-block">
                                                        @{row.username}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4 border-t border-zinc-100 dark:border-zinc-800 pt-3">
                                                <div>
                                                    <p className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1">Total Profit</p>
                                                    <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                                                        ₹{row.totalProfit?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1">Sales Count</p>
                                                    <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                                        {row.totalSalesCount}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center p-8 bg-zinc-50 dark:bg-zinc-800/50 rounded-xl text-zinc-500">
                                        No profit data found.
                                    </div>
                                )}
                            </div>

                            {/* Desktop Table View */}
                            <div className="hidden md:block border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden shadow-sm">
                                <table className="w-full text-left">
                                    <thead className="bg-zinc-50 dark:bg-zinc-800/80 border-b border-zinc-200 dark:border-zinc-800">
                                        <tr>
                                            <th className="px-6 py-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Name</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Username</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider text-right">Total Profit (₹)</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider text-right">Total Sales Count</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800 bg-white dark:bg-zinc-900">
                                        {stats.length > 0 ? (
                                            stats.map((row, idx) => (
                                                <tr key={idx} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                                                    <td className="px-6 py-4 font-medium text-zinc-900 dark:text-zinc-100">
                                                        {row.fullName}
                                                    </td>
                                                    <td className="px-6 py-4 text-zinc-600 dark:text-zinc-400 text-sm">
                                                        <span className="bg-zinc-100 dark:bg-zinc-800 px-2 py-1 rounded-md text-xs font-mono">
                                                            @{row.username}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                                                        ₹{row.totalProfit?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-zinc-600 dark:text-zinc-400 font-medium">
                                                        {row.totalSalesCount}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="px-6 py-12 text-center text-zinc-500 dark:text-zinc-400">
                                                    No profit data found for the selected criteria.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminProfitDashboard;
